import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { useTranslation } from '@hooks';
import { alertActions, surveysActions, channelsActions, agentsActions } from '@actions';
import { agentsService } from '@services';
import { VIEW_TYPE } from '@constants/surveys';
import { ROLE } from '@constants/user';
import { AgentsIcon } from 'assets/images/icons';
import AgentItem from './AgentItem';
import EditModal from './EditModal';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: 16,
        height: 'calc(var(--100vh) - 108px)',
        overflowY: 'auto',
    },
});

const CardsGrid = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    '@media (max-width: 480px)': {
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    },
});

const Agents = () => {
    const { t } = useTranslation();

    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.EDIT });

    const user = useSelector(state => state.auth.user);
    const account = useSelector(state => state.account?.account);
    const data = useSelector(state => state.agents.data);
    const brands = useSelector(state => state.brands.data);
    const dispatch = useDispatch();
    const selectedAgentRef = useRef(null);

    const isSuperAdmin = user.role === ROLE.SUPER_ADMIN;

    useEffect(() => {
        dispatch(agentsActions.get());
        dispatch(surveysActions.getAll());
        dispatch(channelsActions.get());
    }, []);

    const onCloseEditModal = () => {
        setModal({ open: false, type: VIEW_TYPE.EDIT });
        selectedAgentRef.current = null;
    };

    const onClickCard = async (agent, type) => {
        selectedAgentRef.current = agent;
        switch (type) {
            case 'toggleStatus':
                await onToggleStatus();
                break;
            case 'edit':
                setModal({ open: true, type: VIEW_TYPE.EDIT });
                break;
            case 'view':
                setModal({ open: true, type: VIEW_TYPE.VIEW });
                break;
            case 'delete':
                setDeleteModalOpen(true);
                break;
            default:
                break;
        }
    };

    const onToggleStatus = async () => {
        try {
            setIsSubmitting(true);
            const updatedAgent = await agentsService.updateStatusAgent(
                selectedAgentRef.current?.id,
                !!selectedAgentRef.current?.status,
            );
            const agentsCopy = data.slice();
            const updatedAgentIndex = agentsCopy.findIndex(c => c.id === selectedAgentRef.current?.id);
            if (updatedAgentIndex !== -1) {
                agentsCopy[updatedAgentIndex].status = updatedAgent.status;
                dispatch(agentsActions.update(agentsCopy));
            }
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = async () => {
        try {
            await agentsService.deleteAgent(selectedAgentRef.current?.id);
            await dispatch(agentsActions.get());
            await dispatch(channelsActions.get());
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setDeleteModalOpen(false);
            if (modal.open) {
                setModal({ open: false, type: VIEW_TYPE.EDIT });
            }
            selectedAgentRef.current = null;
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
    };

    return (
        <Container>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<AgentsIcon />}
                title={t('Agents.title')}
                subtitle={t('Agents.subtitle')}
            >
                {isSuperAdmin && (
                    <Button variant="contained" onClick={() => setModal({ open: true, type: VIEW_TYPE.EDIT })}>
                        {t('buttons.addAgent')}
                    </Button>
                )}
            </PageHeader>
            <CardsGrid>
                {data.map(item => (
                    <AgentItem
                        key={item.id}
                        data={item}
                        companies={account?.companies}
                        brands={brands}
                        isSuperAdmin={isSuperAdmin}
                        isSubmitting={isSubmitting}
                        onClickCard={onClickCard}
                    />
                ))}
            </CardsGrid>
            {modal.open ? (
                <EditModal
                    modal={modal}
                    onClose={onCloseEditModal}
                    isSuperAdmin={isSuperAdmin}
                    account={account}
                    selectedAgentRef={selectedAgentRef}
                    onDelete={() => setDeleteModalOpen(true)}
                    brands={brands}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </Container>
    );
};

export default Agents;
